import { Box } from '@mui/material';
import { useForm, FormProvider, Resolver, useWatch } from 'react-hook-form';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetProductSuggestionById } from 'hooks/useProductSuggestion';
import useGlobalStyles from 'styles/globalStyles';
import { IPhotoSize } from 'types/types';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import SectionSkeleton from 'components/skeletons/SectionSkeleton';
import { useCreateProduct } from 'hooks/useProduct';
import { snackbarService } from 'services/snackbarService';
import { ProductProvider, useProductContext } from 'context/ProductContext';
import ProductInformationDetails from 'components/Product/ProductDetails/ProductInformationDetails';
import AudienceDetails from 'components/Product/ProductDetails/AudienceDetails';
import ProductDescriptionDetails from 'components/Product/ProductDetails/ProductDescriptionDetails';
import OCRDetails from 'components/Product/ProductDetails/OCRDetails';
import ContentsDetails from 'components/Product/ProductDetails/ContentsDetails';
import BrandDetails from 'components/Product/ProductDetails/BrandDetails';
import UsagesDetails from 'components/Product/ProductDetails/UsagesDetails';
import IngredientsDetails from 'components/Product/ProductDetails/IngredientsDetails';
import AttributesDetails from 'components/Product/ProductDetails/AttributesDetails';
import RankedIngredientsDetails from 'components/Product/ProductDetails/RankedIngredientsDetails';
import WarningsDetails from 'components/Product/ProductDetails/WarningsDetails';
import ProductImagesDetails from 'components/Product/ProductImagesDetails/ProductImagesDetails';
import ProductAvatarDetails from 'components/Product/ProductAvatarDetails/ProductAvatarDetails';
import ProductDisplayImagesDetails from 'components/Product/ProductDisplayImagesDetails/ProductDisplayImagesDetails';
import ProductCategoryDetails from 'components/Product/ProductDetails/ProductCategoryDetails';
import ProductPublishDetails from 'components/Product/ProductDetails/ProductPublishDetails';
import ProductActions from 'components/Product/ProductDetails/ProductActions';
import ChatWidget from 'components/common/ChatWidget/ChatWidget';


export default function ProductDetailsPage() {
  const productId = String(useParams().productId);
  return (
    <ProductProvider productId={productId}>
      <ProductDetailsPageWrapper />
    </ProductProvider>
  )
}

function ProductDetailsPageWrapper() {
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const { product } = useProductContext();
  const globalStyles = useGlobalStyles();

  const sharedProps = useMemo(() => ({
    setCarouselOpen,
    setCarouselImages,
    setCarouselIndex,
  }), []);


  if (!product) {
    return (
      <Box className={globalStyles.container} p={3}>
        <Box>
          <SectionSkeleton count={9} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={4} />
        </Box>

        <Box mt={3}>
          <SectionSkeleton count={7} />
        </Box>

        <Box mt={3} />
      </Box>
    )
  }

  return (
    <>
      <Box className={globalStyles.container} p={3}>

        {/* Images */}
        <Box>
          <ProductImagesDetails shared={sharedProps} />
        </Box>

        {/* OCR Text */}
        <Box mt={3}>
          <OCRDetails />
        </Box>

        {/* Publishing */}
        <Box mt={3}>
          <ProductPublishDetails />
        </Box>

        {/* Product Information */}
        <Box mt={3}>
          <ProductInformationDetails />
        </Box>

        {/* Audience */}
        <Box mt={3}>
          <AudienceDetails />
        </Box>

        {/* Brand */}
        <Box mt={3}>
          <BrandDetails />
        </Box>

        {/* Product Description */}
        <Box mt={3}>
          <ProductDescriptionDetails />
        </Box>

        {/* Product Category */}
        <Box mt={3}>
          <ProductCategoryDetails />
        </Box>

        {/* Contents */}
        <Box mt={3}>
          <ContentsDetails />
        </Box>

        {/* Usages */}
        <Box mt={3}>
          <UsagesDetails />
        </Box>

        {/* Ingredients */}
        <Box mt={3}>
          <IngredientsDetails />
        </Box>

        {/* Ordered ingredients */}
        <Box mt={3}>
          <RankedIngredientsDetails />
        </Box>

        {/* Attributes */}
        <Box mt={3}>
          <AttributesDetails />
        </Box>

        {/* Warnings */}
        <Box mt={3}>
          <WarningsDetails />
        </Box>

        {/* Avatar */}
        <Box mt={3}>
          <ProductAvatarDetails shared={sharedProps} />
        </Box>

        {/* Display images */}
        <Box mt={3}>
          <ProductDisplayImagesDetails shared={sharedProps} />
        </Box>

        {/* What is done to the product as a whole. */}
        <Box mt={4}>
          <ProductActions />
        </Box>

      </Box>


      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

      {/* Sila, asked about this product the way a shopper would ask her. */}
      <ChatWidget chatUrl={`products/${product.id}/chat`} productName={product.info?.name} />
    </>
  );
}
